// React and hooks for component lifecycle and state management
import React, { useEffect, useRef, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
// Authentication context for Autodesk Forge access tokens
import { useAuth } from '../contexts/AuthContext';
// Global state provider for sharing data between viewers
import { useGlobalProvider } from "../contexts/GlobalProvider";
// Utility functions for viewer configuration and tag selection rules
import { rules, tagSelectRules, viewerLinkModes, getViewerDefaults } from '../utilities/viewerUtils';
// Autodesk Forge Viewer extensions for enhanced functionality
import ToolbarExtension from '../viewerExtensions/ToolbarExtension'
import ExternalAttrsExtension from '../viewerExtensions/ExternalAttrsExtension'
import CustomPropertyPanelExtension from '../viewerExtensions/propertypanel'
import AddOns from '../viewerExtensions/AddOns'
// Modal component for importing assets
import ImportAssetsModal from './ImportAssetsModal';
// Fullscreen functionality
import { FullScreen, useFullScreenHandle } from 'react-full-screen';


/**
 * ViewerLeft Component - Left panel Autodesk Forge Viewer
 * 
 * This component renders an Autodesk Forge Viewer instance in the left panel,
 * handles 3D model loading, object selection, and synchronization with the right viewer.
 * 
 * @param {string} urn - The URN of the model to load in the viewer
 * @param {function} onOpenViewer - Callback function called when viewer is opened
 * @param {object} ref - React ref for exposing openViewer/closeViewer methods to parent
 */
const ViewerLeft = forwardRef(({ urn, onOpenViewer }, ref) => {
    // DOM reference to the viewer container div
    const viewerDivLeft = useRef(null);
    // Reference to the Autodesk Viewer instance
    const viewerLeft = useRef(null);
    // Authentication hook for getting Forge access tokens
    const { getAccessToken } = useAuth();
    // State to track if viewer has been initialized
    const [initialized, setInitialized] = useState(false);
    // State for controlling the import assets modal visibility
    const [isModalOpen, setIsModalOpen] = useState(false);
    // State to track if viewer is currently initializing (loading token)
    const [isInitializing, setIsInitializing] = useState(false);
    // Global state for sharing selected tag IDs between left and right viewers
    const { selectedTagIdViewerLeft, setSelectedTagIdViewerRight } = useGlobalProvider();

    // Fullscreen handle for react-full-screen
    const fullScreenHandle = useFullScreenHandle();

    // Get default configuration for left viewer from utility function
    const leftDefaults = getViewerDefaults('left');

    // State for selected viewer linking modes (isolate, color, sync with right viewer)
    const [selectedOptions, setSelectedOptions] = useState(leftDefaults.viewerLinkModes);
    // Ref to access selectedOptions in event handlers without stale closure issues
    const selectedOptionsRef = useRef(selectedOptions);
    // State to track if the 3D model has finished loading
    const [viewerLoaded, setViewerLoaded] = useState(false);

    // State object for tracking which viewer actions are enabled
    const [actions, setActions] = useState({
        // TODO rename to link2OtherViewerAndAsset
        link2RightAndAsset: leftDefaults.viewerLinkModes.includes('link2RightAndAsset'),
        isolateSelection: leftDefaults.viewerLinkModes.includes('isolateSelection'),
        selectionColourRed: leftDefaults.viewerLinkModes.includes('selectionColourRed')
    });

    // State for the currently selected rule for extracting tag IDs from object properties
    const [selectedRule, setSelectedRule] = useState(leftDefaults.rules);
    // Ref to access selectedRule in event handlers without stale closure issues
    const selectedRuleRef = useRef(selectedRule);
    // State to prevent infinite loops when programmatically selecting/isolating objects
    const [isProgrammaticIsolation, setIsProgrammaticIsolation] = useState(false);
    // Ref to access isProgrammaticIsolation in event handlers
    const isProgrammaticIsolationRef = useRef(isProgrammaticIsolation);


    // Effect: Handle selection synchronization from right viewer to left viewer
    // When an asset is selected in the right viewer, search and highlight it in the left viewer
    useEffect(() => {
        if (viewerLeft.current && (selectedOptionsRef.current.includes('link2RightAndAsset')) && selectedTagIdViewerLeft) {
            console.log('Selected Tag left viewer:', selectedTagIdViewerLeft);
            performSearchIsolateAndFit(selectedTagIdViewerLeft);
        }
    }, [selectedTagIdViewerLeft]);


    // Effect: Sync isProgrammaticIsolationRef with isProgrammaticIsolation state
    // This ensures event handlers always have access to the current value
    useEffect(() => {
        isProgrammaticIsolationRef.current = isProgrammaticIsolation;
    }, [isProgrammaticIsolation]);

    // Effect: Sync selectedOptionsRef with selectedOptions state
    // This ensures event handlers always have access to the current selected options
    useEffect(() => {
        selectedOptionsRef.current = selectedOptions;
    }, [selectedOptions]);


    // Effect: Initialize the Autodesk Forge Viewer when component is ready
    // Sets up the viewer instance, resize observer, and cleanup functions
    useEffect(() => {
        if (!initialized || !viewerDivLeft.current) return;

        const { Autodesk } = window;
        initializeViewer(Autodesk);


        // Set up resize observer to handle container size changes
        const resizeObserver = new ResizeObserver(() => {
            resizeViewer();
        });

        resizeObserver.observe(viewerDivLeft.current);

        // Cleanup function: dispose viewer and disconnect observer
        return () => {
            if (viewerLeft.current) {
                viewerLeft.current.finish();
                viewerLeft.current = null;
            }
            resizeObserver.disconnect();
        };
    }, [initialized]);


    // Effect: Log URN changes for debugging purposes
    useEffect(() => {
        console.log('ViewerLeft: URN prop changed to:', urn);
    }, [urn]);

    // Effect: Load new document when URN changes or viewer is initialized
    // This triggers the loading of a new 3D model in the viewer
    useEffect(() => {
        if (viewerLeft.current && urn) {
            const { Autodesk } = window;
            loadDocument(Autodesk, urn);
        }
    }, [urn, initialized]);


    /**
     * Initialize the Autodesk Forge Viewer with authentication and configuration
     * Sets up the viewer environment, token handling, and event listeners
     * 
     * @param {object} Autodesk - The Autodesk Viewing namespace from the global window object
     */
    const initializeViewer = (Autodesk) => {
        // Viewer initialization options
        const options = {
            // TODO check if local may avoid corse issues:
            //env: "Local",
            useConsolidation: false, // Enable geometry consolidation for better performance
            useADP: false, // Disable Autodesk Data Platform
            env: 'AutodeskProduction', // Use production Autodesk environment
            // Token provider function with retry logic for robust authentication
            getAccessToken: async (onTokenReady) => {
                let retryCount = 0;
                const maxRetries = 3;

                // Retry loop for token acquisition
                while (retryCount < maxRetries) {
                    try {
                        console.log(`[ViewerLeft] Requesting access token (attempt ${retryCount + 1}/${maxRetries})...`);
                        const token = await getAccessToken();
                        console.log('[ViewerLeft] Access token received:', token ? 'Yes' : 'No');
                        if (!token) {
                            throw new Error('No token received');
                        }
                        const timeInSeconds = 3600; // Token validity duration
                        onTokenReady(token, timeInSeconds);
                        console.log('[ViewerLeft] Token provided to viewer');
                        return; // Success, exit the retry loop
                    } catch (error) {
                        retryCount++;
                        console.error(`[ViewerLeft] Error fetching access token (attempt ${retryCount}):`, error);

                        if (retryCount < maxRetries) {
                            console.log(`[ViewerLeft] Retrying in ${retryCount * 1000}ms...`);
                            await new Promise(resolve => setTimeout(resolve, retryCount * 1000));
                        } else {
                            console.error('[ViewerLeft] All token fetch attempts failed');
                        }
                    }
                }
            },
        };

        //         const reRegisterExtension = () => {
        //             const { Autodesk } = window;
        //             const extMgr = Autodesk.Viewing.theExtensionManager;
        //             // Remove the stale registration (use with caution as this is not public API)
        //             if (extMgr._extensions && extMgr._extensions['MyExtension']) {
        //                 delete extMgr._extensions['MyExtension'];
        //             }
        //             // Re-register your extension (assuming window.MyExtension is defined)
        //             extMgr.registerExtension('MyExtension', window.MyExtension);
        //         };
        //
        // // Call reRegisterExtension() before initializing the viewer
        //         reRegisterExtension();



        // Available extensions: 'ViewCubeUi', 'Measure', 'Autodesk.BIM360.Minimap', 'Autodesk.Viewing.ZoomWindow', 'DefaultTools.NavTools'
        // Initialize the Autodesk Viewer with the configured options
        Autodesk.Viewing.Initializer(options, () => {
            const config = {
                // Extensions are loaded later after model loads to avoid timing issues
                // extensions: ['Autodesk.Viewing.ZoomWindow'],
            };
            // Create a new 3D viewer instance with GUI controls
            viewerLeft.current = new Autodesk.Viewing.GuiViewer3D(viewerDivLeft.current, config);
            viewerLeft.current.start();

            // Load the initial document if URN is provided
            loadDocument(Autodesk, urn);

            // Event listener for object isolation events (when objects are isolated in the viewer)
            viewerLeft.current.addEventListener(Autodesk.Viewing.ISOLATE_EVENT, function (event) {
                console.log('Selection changed event isProgrammaticIsolation:', isProgrammaticIsolationRef.current);

                const dbIds = viewerLeft.current.getIsolatedNodes();
                console.log('isolated nodes', dbIds);
                // Only process user-initiated isolations, not programmatic ones
                if (!isProgrammaticIsolationRef.current && dbIds > 0) {
                    processDbId(viewerLeft.current, dbIds);
                }
            });

            // Event listener for object selection events
            viewerLeft.current.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, function (event) {
                console.log('Selection changed event isProgrammaticIsolation:', isProgrammaticIsolationRef.current);
                // Only process user-initiated selections, not programmatic ones
                if (!isProgrammaticIsolationRef.current && event.dbIdArray.length > 0) {
                    processDbId(viewerLeft.current, event.dbIdArray[0]);
                }
            });
        });
    };
    /**
     * Search for objects by tag ID, isolate them, and fit the view
     * This function is called when synchronizing selection from the right viewer
     * 
     * @param {string} tagId - The tag ID to search for in the 3D model
     */
    const performSearchIsolateAndFit = (tagId) => {
        if (!viewerLeft.current) return;

        // Set flag to prevent infinite loops from selection events
        setIsProgrammaticIsolation(true);
        console.log('Performing search isolate and fit isProgrammaticIsolationRef :', !isProgrammaticIsolationRef.current);

        // Search for objects matching the tag ID
        viewerLeft.current.search(tagId, (ids) => {
            // Isolate objects if isolation mode is enabled and no objects are currently isolated
            if (selectedOptions.includes('isolateSelection') && !viewerLeft.current.getIsolatedNodes()) {
                viewerLeft.current.isolate(ids);
            }
            // Select the found objects (important for applying selection color)
            viewerLeft.current.select(ids);

            // Fit the camera view to show the selected objects
            if (ids.length > 0) {
                viewerLeft.current.fitToView(ids);

                // Commented out: Advanced camera manipulation for 2D/3D models
                // This code could be used for custom zoom levels and camera positioning
                // let is3D = viewerLeft.current.model.is3d();
                // let camera = viewerLeft.current.getCamera();
                // let originalFov = camera.fov;
                // ... (camera manipulation code)
            }

            // Reset the programmatic isolation flag
            setIsProgrammaticIsolation(false);
            // Note: Selection is done above to avoid endless loop with SELECTION_CHANGED_EVENT
        });
    };

    /**
     * Handle changes to the tag extraction rule dropdown
     * Updates the selected rule for extracting tag IDs from object properties
     * 
     * @param {Event} event - The select change event
     */
    /**
     * Handle dropdown change for tag extraction rules
     * Updates the selected rule used to extract tag IDs from object properties
     * @param {Event} event - The select dropdown change event
     */
    const handleRuleChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedRule(selectedValue);
    };

    // Effect: Sync selectedRuleRef with selectedRule state
    // This ensures event handlers always have access to the current rule
    useEffect(() => {
        // console.log('Selected rule updated useeffect:', selectedRule);
        selectedRuleRef.current = selectedRule;
    }, [selectedRule]); // This effect runs every time selectedRule changes

    /**
     * Process a selected object by its database ID
     * Extracts properties, applies isolation if enabled, and synchronizes with right viewer
     * 
     * @param {object} viewer - The Autodesk Viewer instance
     * @param {number} dbId - The database ID of the selected object
     */
    const processDbId = (viewer, dbId) => {
        console.log('Selected isProgrammaticIsolation:', isProgrammaticIsolation);

        // Get properties of the selected object
        viewer.getProperties(dbId, (props) => {
            console.log('Properties of the selected object processDbId viewer left:', props);

            // Isolate the selected object if isolation mode is enabled
            if (selectedOptionsRef.current.includes('isolateSelection')) {
                viewerLeft.current.isolate(dbId);
            }

            // Extract tag ID from object properties using the selected rule
            const extractedString = tagSelectRules(props, selectedRuleRef.current);

            if (extractedString) {
                console.log('Extracted string:', extractedString);
                // Update the right viewer with the extracted tag ID
                setSelectedTagIdViewerRight(extractedString);
            } else {
                console.warn('tagSelectRules did not return a valid string. Result:', extractedString);
            }
        }, (error) => {
            console.error('Error getting properties:', error);
        });
    };


    /**
     * Load a 3D document/model into the viewer
     * Handles document loading, geometry setup, and extension loading
     * 
     * @param {object} Autodesk - The Autodesk Viewing namespace
     * @param {string} urn - The URN of the document to load
     */
    const loadDocument = (Autodesk, urn) => {
        console.log('Load urn:', urn);

        // Clear property keys from previous model before loading new one
        setAvailablePropertyKeys([]);
        console.log('Property keys cleared for new model');

        // Load the document from Autodesk servers
        Autodesk.Viewing.Document.load(
            `urn:${urn}`,
            (doc) => {
                // Get the default 3D viewable from the document
                const viewable = doc.getRoot().getDefaultGeometry();

                // Load the document node into the viewer
                viewerLeft.current.loadDocumentNode(doc, viewable).then(() => {
                    // Set selection mode to select complete objects instead of just surfaces
                    viewerLeft.current.setSelectionMode(Autodesk.Viewing.SelectionMode.LAST_OBJECT)

                    // Listen for geometry loaded event to know when model is ready
                    viewerLeft.current.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, function () {
                        setViewerLoaded(true);
                        loadCustomExtensions(); // Load viewer extensions
                        performSearchIsolateAndFit(selectedTagIdViewerLeft); // Apply any pending selections
                    });
                });
            },
            (errorCode) => {
                console.error('Error loading document:', errorCode);
            }
        );
    };

    /**
     * Resize the viewer to fit its container
     * Called when the container size changes via ResizeObserver
     */
    const resizeViewer = useCallback(() => {
        if (viewerLeft.current) {
            viewerLeft.current.resize();
        }
    }, []);


    /**
     * Close and cleanup the viewer instance
     * Unloads extensions, removes event listeners, and disposes resources
     */
    const closeViewer = () => {
        setInitialized(false);
        const { Autodesk } = window;

        // Clear property keys when closing viewer
        setAvailablePropertyKeys([]);
        console.log('Property keys cleared on viewer close');

        if (viewerLeft.current) {
            // Unload all loaded extensions to prevent memory leaks
            const loadedExtensions = viewerLeft.current.impl && viewerLeft.current.impl.extensions;
            if (loadedExtensions) {
                Object.keys(loadedExtensions).forEach((extName) => {
                    viewerLeft.current.unloadExtension(extName);
                });
            }

            // Remove event listeners to prevent memory leaks
            viewerLeft.current.removeEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT);
            viewerLeft.current.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT);

            // Properly dispose of the viewer instance
            viewerLeft.current.finish();

            // Clear the DOM container
            // TODO: add similar cleanup to right viewer
            if (viewerLeft.current.container) {
                viewerLeft.current.container.innerHTML = '';
            }

            // Remove the viewer reference
            viewerLeft.current = null;
        }
    };

    /**
     * Open and initialize the viewer
     * Pre-fetches authentication token and initializes the Autodesk Viewer
     */
    const openViewer = async () => {
        try {
            setIsInitializing(true);
            console.log('[ViewerLeft] Pre-fetching access token before viewer initialization...');

            // Pre-fetch the authentication token to ensure it's available when the viewer needs it
            const token = await getAccessToken();
            if (!token) {
                console.error('[ViewerLeft] Failed to get access token, cannot open viewer');
                setIsInitializing(false);
                return;
            }
            console.log('[ViewerLeft] Token pre-fetched successfully, initializing viewer...');

            setInitialized(true);
            setIsInitializing(false);

            // Notify parent component that viewer is being opened
            if (onOpenViewer) {
                onOpenViewer();
            }
        } catch (error) {
            console.error('[ViewerLeft] Failed to pre-fetch token:', error);
            setIsInitializing(false);
        }
    };

    // Expose openViewer and closeViewer functions to parent component via ref
    // This allows parent components to programmatically control the viewer
    useImperativeHandle(ref, () => ({
        openViewer,
        closeViewer
    }));

    /**
     * Handle changes to viewer linking mode options
     * Toggles options like isolation, color changes, and viewer synchronization
     * 
     * @param {Event} event - The select change event
     */
    const handleSelectOptions = (event) => {
        const value = event.target.value;

        // Toggle the selected option in the array
        setSelectedOptions((prevSelected) =>
            prevSelected.includes(value)
                ? prevSelected.filter((mode) => mode !== value)
                : [...prevSelected, value]
        );

        // Update the corresponding action state
        setActions((prevActions) => ({
            ...prevActions,
            [value]: !prevActions[value]
        }));
    };
    // Effect: Log actions state changes for debugging
    useEffect(() => {
        console.log('Actions state updated viewer left:', actions);
    }, [actions]);

    // Effect: Apply selection color changes based on user preferences
    // Updates both 3D and 2D selection colors when the color option is toggled
    useEffect(() => {
        if (!viewerLoaded || !viewerLeft.current) {
            console.warn('Viewer left not fully loaded yet.');
            return;
        }

        // Apply red selection color if the option is enabled
        if (selectedOptionsRef.current.includes('selectionColourRed')) {
            viewerLeft.current.setSelectionColor((0xff0000), window.Autodesk.Viewing.SelectionType.MIXED);
            viewerLeft.current.set2dSelectionColor((0xff0000), 1);
            console.log("selection color set to red");
        } else {
            // Use default blue selection color
            viewerLeft.current.setSelectionColor((0x82B4FA), window.Autodesk.Viewing.SelectionType.MIXED);
            viewerLeft.current.set2dSelectionColor((0x82B4FA), 1);
            console.log("selection color back to standard")
        }
    }, [selectedOptionsRef.current, viewerLoaded, actions]);

    // Effect: Handle viewer resize when entering/exiting fullscreen
    useEffect(() => {
        if (viewerLeft.current && fullScreenHandle.active !== undefined) {
            // Small delay to ensure DOM has updated
            setTimeout(() => {
                resizeViewer();
            }, 100);
        }
    }, [fullScreenHandle.active, resizeViewer]);
    /**
     * Set theming color for one or more database IDs
     * Updated to match the working pattern from setThemingColorForDbId1
     * 
     * @param {number|number[]} dbIds - Single dbId or array of dbIds to apply theming color to
     * @param {object} [colorOverride] - Optional color override (defaults to yellow)
     */
    const setThemingColor = (dbIds, colorOverride = null) => {
        if (!viewerLeft.current || !viewerLoaded) return console.warn('Viewer not ready');

        const dbIdArray = Array.isArray(dbIds) ? dbIds : [dbIds];
        if (dbIdArray.length === 0) return;

        const model = viewerLeft.current.model;
        const THREE = window.Autodesk?.Viewing?.Private?.THREE || window.THREE;

        let color = colorOverride;
        if (!color) {
            color = THREE ? new THREE.Vector4(1, 1, 0, 1) : { x: 1, y: 1, z: 0, w: 1 };
        }

        // Apply the working pattern to each dbId individually
        dbIdArray.forEach(dbId => {
            try {
                viewerLeft.current.setThemingColor(dbId, color, model, true);
                viewerLeft.current.impl.invalidate(true);
                console.log(`Set theming color for dbId ${dbId}`);
            } catch (err) {
                console.error('Error applying theming color:', err);
            }
        });
    };


    /**
     * Clear all theming colors from the model
     * Removes all previously applied theming colors
     */
    const clearThemingColors = () => {
        if (!viewerLeft.current || !viewerLoaded) {
            console.warn('Viewer not ready for theming color operations');
            return;
        }

        try {
            const model = viewerLeft.current.model;

            // Clear all theming colors
            viewerLeft.current.clearThemingColors(model);
            console.log('All theming colors cleared');

            // Request a refresh to show the changes
            viewerLeft.current.impl.invalidate(true);
        } catch (error) {
            console.error('Error clearing theming colors:', error);
        }
    };

    /**
     * Set pink color for currently selected objects
     * Uses the exact same pattern as the working setThemingColorForDbId1 example
     */
    const setSelectedToPink = () => {
        if (!viewerLeft.current || !viewerLoaded) return console.warn('Viewer not ready');

        // Get currently selected objects
        const selectedDbIds = viewerLeft.current.getSelection();

        if (!selectedDbIds || selectedDbIds.length === 0) {
            return console.warn('No objects selected');
        }

        const model = viewerLeft.current.model;
        const THREE = window.Autodesk?.Viewing?.Private?.THREE || window.THREE;
        const pinkColor = THREE ? new THREE.Vector4(1, 0.75, 0.8, 1) : { x: 1, y: 0.75, z: 0.8, w: 1 };

        try {
            selectedDbIds.forEach(dbId => {
                viewerLeft.current.setThemingColor(dbId, pinkColor, model, true);
                viewerLeft.current.impl.invalidate(true);
                console.log(`Set theming color for dbId ${dbId}`);
            });
        } catch (err) {
            console.error('Error applying theming color:', err);
        }
    };
    const setThemingColorForDbId1 = () => {
        if (!viewerLeft.current || !viewerLoaded) return console.warn('Viewer not ready');

        const dbId = 497;
        const model = viewerLeft.current.model;

        const THREE = window.Autodesk?.Viewing?.Private?.THREE || window.THREE;
        const color = THREE ? new THREE.Vector4(1, 1, 0, 1) : { x: 1, y: 1, z: 0, w: 1 };

        try {
            viewerLeft.current.setThemingColor(dbId, color, model, true);
            viewerLeft.current.impl.invalidate(true);
            console.log(`Set theming color for dbId ${dbId}`);
        } catch (err) {
            console.error('Error applying theming color:', err);
        }
    };





    /**
     * Get all available properties from the viewer model
     * Uses standard Viewer API for maximum compatibility
     */
    const getAllProperties = () => {
        if (!viewerLeft.current || !viewerLoaded) {
            console.warn('Viewer not ready for property operations');
            return;
        }

        try {
            const model = viewerLeft.current.model;
            const instanceTree = model.getInstanceTree();

            if (!instanceTree) {
                console.warn('Instance tree not available');
                return;
            }

            const results = [];
            let processedCount = 0;
            let totalNodes = 0;

            // Get all node IDs from the instance tree
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                totalNodes++;
            }, true);

            console.log(`Starting to retrieve properties for ${totalNodes} objects...`);

            // Enumerate through all nodes and get their properties
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                viewerLeft.current.getProperties(dbId, (props) => {
                    results.push({ dbId, props });
                    processedCount++;

                    // Log progress every 100 items or when complete
                    if (processedCount % 100 === 0 || processedCount === totalNodes) {
                        console.log(`Progress: ${processedCount}/${totalNodes} properties retrieved`);
                    }

                    // When all properties are collected, display the results
                    if (processedCount === totalNodes) {
                        console.log("All properties:", results);

                        // Display a summary
                        const summary = results.map(item => ({
                            dbId: item.dbId,
                            name: item.props.name || 'Unnamed',
                            propertyCount: item.props.properties ? item.props.properties.length : 0
                        }));

                        console.log("Properties summary:", summary);
                        console.log(`Successfully retrieved properties for ${results.length} objects`);
                    }
                }, (error) => {
                    console.error(`Error getting properties for dbId ${dbId}:`, error);
                    processedCount++;
                });
            }, true);

        } catch (error) {
            console.error('Error in getAllProperties function:', error);
        }
    };

    /**
     * Fallback method to get all properties using standard Viewer API
     * Used when property database approach fails
     */
    const getAllPropertiesStandard = () => {
        try {
            const model = viewerLeft.current.model;
            const instanceTree = model.getInstanceTree();

            if (!instanceTree) {
                console.warn('Instance tree not available');
                return;
            }

            const results = [];
            let processedCount = 0;
            let totalNodes = 0;

            // Get all node IDs from the instance tree
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                totalNodes++;
            }, true);

            console.log(`Fallback: Starting to retrieve properties for ${totalNodes} objects...`);

            // Enumerate through all nodes and get their properties
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                viewerLeft.current.getProperties(dbId, (props) => {
                    results.push({ dbId, props });
                    processedCount++;

                    // When all properties are collected, display the results
                    if (processedCount === totalNodes) {
                        console.log("All properties (fallback method):", results);
                        console.log(`Successfully retrieved properties for ${results.length} objects`);
                    }
                }, (error) => {
                    console.error(`Error getting properties for dbId ${dbId}:`, error);
                    processedCount++;
                });
            }, true);

        } catch (error) {
            console.error('Error in getAllPropertiesStandard function:', error);
        }
    };

    /**
     * Get all available display names from the viewer model
     * Retrieves display names for all objects in the model
     */
    const getAllDisplayNames = () => {
        if (!viewerLeft.current || !viewerLoaded) {
            console.warn('Viewer not ready for display name operations');
            return;
        }

        try {
            const model = viewerLeft.current.model;
            const instanceTree = model.getInstanceTree();

            if (!instanceTree) {
                console.warn('Instance tree not available');
                return;
            }

            const displayNames = [];
            let processedCount = 0;
            let totalNodes = 0;

            // Get all node IDs from the instance tree
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                totalNodes++;
            }, true);

            console.log(`Starting to retrieve display names for ${totalNodes} objects...`);

            // Enumerate through all nodes and get their display names
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                viewerLeft.current.getProperties(dbId, (props) => {
                    // Extract display name from properties
                    const displayName = props.name || props.displayName || `Object_${dbId}`;

                    displayNames.push({
                        dbId: dbId,
                        displayName: displayName,
                        // Also include any other name-related properties
                        externalId: props.externalId || null
                    });

                    processedCount++;

                    // Log progress every 100 items or when complete
                    if (processedCount % 100 === 0 || processedCount === totalNodes) {
                        console.log(`Progress: ${processedCount}/${totalNodes} display names retrieved`);
                    }

                    // When all display names are collected, display the results
                    if (processedCount === totalNodes) {
                        console.log("All display names:", displayNames);

                        // Create a sorted list of unique display names
                        const uniqueDisplayNames = [...new Set(displayNames.map(item => item.displayName))].sort();
                        console.log("Unique display names:", uniqueDisplayNames);
                        console.log(`Found ${uniqueDisplayNames.length} unique display names out of ${displayNames.length} total objects`);

                        // Group by display name to show duplicates
                        const groupedByName = displayNames.reduce((acc, item) => {
                            if (!acc[item.displayName]) {
                                acc[item.displayName] = [];
                            }
                            acc[item.displayName].push(item.dbId);
                            return acc;
                        }, {});

                        console.log("Display names grouped by name:", groupedByName);
                    }
                }, (error) => {
                    console.error(`Error getting properties for dbId ${dbId}:`, error);
                    processedCount++;
                });
            }, true);

        } catch (error) {
            console.error('Error in getAllDisplayNames function:', error);
        }
    };

    /**
     * Get all unique property keys from the viewer model
     * Uses standard Viewer API for maximum compatibility
     */
    const getAllPropertyKeys = () => {
        console.log('getAllPropertyKeys called, viewerLoaded:', viewerLoaded, 'viewer:', !!viewerLeft.current);

        if (!viewerLeft.current || !viewerLoaded) {
            console.warn('Viewer not ready for property key operations');
            return;
        }

        try {
            const model = viewerLeft.current.model;
            const instanceTree = model.getInstanceTree();

            if (!instanceTree) {
                console.warn('Instance tree not available');
                return;
            }

            const allPropertyKeys = new Set();
            let processedCount = 0;
            let totalNodes = 0;

            // Get all node IDs from the instance tree
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                totalNodes++;
            }, true);

            console.log(`Starting to retrieve property keys from ${totalNodes} objects...`);

            // Enumerate through all nodes and collect property keys
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                viewerLeft.current.getProperties(dbId, (props) => {
                    // Add property keys from the properties array
                    if (props.properties) {
                        props.properties.forEach(prop => {
                            // Add both attributeName and displayName as potential keys
                            if (prop.attributeName) allPropertyKeys.add(prop.attributeName);
                            if (prop.displayName) allPropertyKeys.add(prop.displayName);
                        });
                    }

                    processedCount++;

                    // Log progress and complete when done
                    if (processedCount % 100 === 0 || processedCount === totalNodes) {
                        console.log(`Progress: ${processedCount}/${totalNodes} objects processed for property keys`);
                    }

                    if (processedCount === totalNodes) {
                        const propertyKeys = Array.from(allPropertyKeys).sort();
                        console.log("All available property keys:", propertyKeys);
                        console.log(`Found ${propertyKeys.length} unique property keys`);

                        // Store property keys in state for dropdown use
                        setAvailablePropertyKeys(propertyKeys);
                    }
                }, (error) => {
                    console.error(`Error getting properties for dbId ${dbId}:`, error);
                    processedCount++;
                });
            }, true);

        } catch (error) {
            console.error('Error in getAllPropertyKeys function:', error);
        }
    };

    /**
     * Fallback method to get property keys using standard API
     */
    const getAllPropertyKeysStandard = () => {
        try {
            const model = viewerLeft.current.model;
            const instanceTree = model.getInstanceTree();

            if (!instanceTree) {
                console.warn('Instance tree not available');
                return;
            }

            const allPropertyKeys = new Set();
            let processedCount = 0;
            let totalNodes = 0;

            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                totalNodes++;
            }, true);

            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                viewerLeft.current.getProperties(dbId, (props) => {
                    // Add property keys from the properties array
                    if (props.properties) {
                        props.properties.forEach(prop => {
                            allPropertyKeys.add(prop.attributeName);
                            allPropertyKeys.add(prop.displayName);
                        });
                    }

                    processedCount++;

                    if (processedCount === totalNodes) {
                        const propertyKeys = Array.from(allPropertyKeys).sort();
                        console.log("All available property keys (fallback):", propertyKeys);
                        setAvailablePropertyKeys(propertyKeys);
                    }
                }, (error) => {
                    processedCount++;
                });
            }, true);

        } catch (error) {
            console.error('Error in getAllPropertyKeysStandard function:', error);
        }
    };

    /**
     * Search for objects by property key and value
     * Uses standard Viewer API for maximum compatibility
     * @param {string} propertyKey - The property key to search for
     * @param {string} searchValue - The value to match
     */
    const searchByProperty = (propertyKey, searchValue) => {
        if (!viewerLeft.current || !viewerLoaded || !propertyKey || !searchValue) {
            console.warn('Invalid search parameters');
            return;
        }

        try {
            const model = viewerLeft.current.model;
            const instanceTree = model.getInstanceTree();

            if (!instanceTree) {
                console.warn('Instance tree not available');
                return;
            }

            const matchingDbIds = [];
            let processedCount = 0;
            let totalNodes = 0;

            // Get all node IDs from the instance tree
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                totalNodes++;
            }, true);

            console.log(`Searching ${totalNodes} objects for "${propertyKey}" = "${searchValue}" (${matchType} match)...`);

            // Enumerate through all nodes and search for matching properties
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                viewerLeft.current.getProperties(dbId, (props) => {
                    // Search through the properties array
                    if (props.properties) {
                        props.properties.forEach(prop => {
                            // Check both attributeName and displayName
                            const keyMatches = prop.attributeName === propertyKey || prop.displayName === propertyKey;

                            if (keyMatches && prop.displayValue) {
                                const propValue = String(prop.displayValue).toLowerCase();
                                const searchValueLower = searchValue.toLowerCase();

                                // Check match based on selected match type
                                const isMatch = matchType === 'exact'
                                    ? propValue === searchValueLower
                                    : propValue.includes(searchValueLower);

                                if (isMatch) {
                                    matchingDbIds.push({
                                        dbId: dbId,
                                        propertyName: prop.displayName || prop.attributeName,
                                        value: prop.displayValue
                                    });
                                }
                            }
                        });
                    }

                    processedCount++;

                    // When all objects are processed, show results
                    if (processedCount === totalNodes) {
                        console.log(`Found ${matchingDbIds.length} objects matching "${propertyKey}" = "${searchValue}":`, matchingDbIds);

                        // Store search results for UI display
                        setLastSearchResults({
                            propertyKey,
                            searchValue,
                            matchType,
                            resultCount: matchingDbIds.length,
                            timestamp: new Date()
                        });

                        // Extract just the dbIds for viewer operations
                        const dbIds = matchingDbIds.map(match => match.dbId);

                        if (dbIds.length > 0) {
                            // Isolate and select the matching objects
                            if (selectedOptionsRef.current.includes('isolateSelection')) {
                                viewerLeft.current.isolate(dbIds);
                            }
                            viewerLeft.current.select(dbIds);
                            viewerLeft.current.fitToView(dbIds);

                            console.log(`Selected and focused on ${dbIds.length} matching objects`);
                        } else {
                            console.log('No matching objects found');
                        }
                    }
                }, (error) => {
                    console.error(`Error getting properties for dbId ${dbId}:`, error);
                    processedCount++;
                });
            }, true);

        } catch (error) {
            console.error('Error in searchByProperty function:', error);
        }
    };

    /**
     * Convert hex color to viewer color format
     * @param {string} hexColor - Hex color string (e.g., '#ff0000')
     * @returns {object} Color object for viewer
     */
    const hexToViewerColor = (hexColor) => {
        // Remove # if present
        const hex = hexColor.replace('#', '');

        // Convert hex to RGB
        const r = parseInt(hex.substr(0, 2), 16) / 255;
        const g = parseInt(hex.substr(2, 2), 16) / 255;
        const b = parseInt(hex.substr(4, 2), 16) / 255;

        // Return color object in the same format as working examples
        const THREE = window.Autodesk?.Viewing?.Private?.THREE || window.THREE;
        return THREE ? new THREE.Vector4(r, g, b, 1) : { x: r, y: g, z: b, w: 1 };
    };

    /**
     * Color the currently selected objects using the selected color from search popup
     * Uses the same color as selected in the search color picker
     */
    const colorSelectedObjects = () => {
        if (!viewerLeft.current || !viewerLoaded) return console.warn('Viewer not ready');

        // Get currently selected objects
        const selectedDbIds = viewerLeft.current.getSelection();

        if (!selectedDbIds || selectedDbIds.length === 0) {
            return console.warn('No objects selected');
        }

        // Convert hex color to viewer format using the same function as search results
        const colorObject = hexToViewerColor(selectedColor);

        // Use the existing setThemingColor function to color the selected objects
        setThemingColor(selectedDbIds, colorObject);
        console.log(`Colored ${selectedDbIds.length} selected objects with color ${selectedColor}`);
    };

    /**
     * Color the search results using the theming color functionality
     * Colors all objects from the last search results
     */
    const colorSearchResults = () => {
        if (!lastSearchResults || lastSearchResults.resultCount === 0) {
            console.warn('No search results to color');
            return;
        }

        // We need to get the dbIds from the last search
        // Since we don't store them in lastSearchResults, we need to perform the search again
        // to get the dbIds, then color them
        if (!viewerLeft.current || !viewerLoaded) {
            console.warn('Viewer not ready for coloring operations');
            return;
        }

        try {
            const model = viewerLeft.current.model;
            const instanceTree = model.getInstanceTree();

            if (!instanceTree) {
                console.warn('Instance tree not available');
                return;
            }

            const matchingDbIds = [];
            let processedCount = 0;
            let totalNodes = 0;

            // Get all node IDs from the instance tree
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                totalNodes++;
            }, true);

            console.log(`Coloring search results: "${lastSearchResults.propertyKey}" = "${lastSearchResults.searchValue}" (${lastSearchResults.matchType} match)`);

            // Enumerate through all nodes and find matching properties
            instanceTree.enumNodeChildren(instanceTree.getRootId(), (dbId) => {
                viewerLeft.current.getProperties(dbId, (props) => {
                    // Search through the properties array
                    if (props.properties) {
                        props.properties.forEach(prop => {
                            // Check both attributeName and displayName
                            const keyMatches = prop.attributeName === lastSearchResults.propertyKey ||
                                prop.displayName === lastSearchResults.propertyKey;

                            if (keyMatches && prop.displayValue) {
                                const propValue = String(prop.displayValue).toLowerCase();
                                const searchValueLower = lastSearchResults.searchValue.toLowerCase();

                                // Check match based on the stored match type
                                const isMatch = lastSearchResults.matchType === 'exact'
                                    ? propValue === searchValueLower
                                    : propValue.includes(searchValueLower);

                                if (isMatch) {
                                    matchingDbIds.push(dbId);
                                }
                            }
                        });
                    }

                    processedCount++;

                    // When all objects are processed, color the results
                    if (processedCount === totalNodes) {
                        if (matchingDbIds.length > 0) {
                            // Convert hex color to viewer format using the fixed function
                            const colorObject = hexToViewerColor(selectedColor);
                            // Use the existing setThemingColor function to color the results with selected color
                            setThemingColor(matchingDbIds, colorObject);
                            console.log(`Colored ${matchingDbIds.length} objects from search results with color ${selectedColor}`);
                        } else {
                            console.log('No matching objects found to color');
                        }
                    }
                }, (error) => {
                    console.error(`Error getting properties for dbId ${dbId}:`, error);
                    processedCount++;
                });
            }, true);

        } catch (error) {
            console.error('Error in colorSearchResults function:', error);
        }
    };

    // State for property search interface
    const [availablePropertyKeys, setAvailablePropertyKeys] = useState([]);
    const [selectedPropertyKey, setSelectedPropertyKey] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [isPropertySearchModalOpen, setIsPropertySearchModalOpen] = useState(false);
    const [matchType, setMatchType] = useState('partial'); // 'partial' or 'exact'
    const [lastSearchResults, setLastSearchResults] = useState(null); // Track last search results
    const [panelPosition, setPanelPosition] = useState({ x: 100, y: 100 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [selectedColor, setSelectedColor] = useState('#ffff00'); // Default yellow color
    const [showColorPicker, setShowColorPicker] = useState(false);

    // Auto-load property keys when viewer is loaded
    useEffect(() => {
        if (viewerLoaded && viewerLeft.current) {
            console.log('Auto-loading property keys...');
            getAllPropertyKeys();
        }
    }, [viewerLoaded]);

    // Auto-load property keys when modal opens (if not already loaded)
    useEffect(() => {
        if (isPropertySearchModalOpen && viewerLoaded && viewerLeft.current && availablePropertyKeys.length === 0) {
            console.log('Auto-loading property keys on modal open...');
            getAllPropertyKeys();
        }
    }, [isPropertySearchModalOpen, viewerLoaded, availablePropertyKeys.length]);

    // Clear search results when search parameters change
    useEffect(() => {
        if (lastSearchResults &&
            (lastSearchResults.propertyKey !== selectedPropertyKey ||
                lastSearchResults.searchValue !== searchValue ||
                lastSearchResults.matchType !== matchType)) {
            setLastSearchResults(null);
        }
    }, [selectedPropertyKey, searchValue, matchType, lastSearchResults]);

    // Handle mouse down for dragging
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - panelPosition.x,
            y: e.clientY - panelPosition.y
        });
    };

    // Handle mouse move for dragging
    const handleMouseMove = useCallback((e) => {
        if (isDragging) {
            setPanelPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        }
    }, [isDragging, dragOffset]);

    // Handle mouse up to stop dragging
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Add global mouse event listeners for dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Close color picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showColorPicker && !event.target.closest('.color-picker-container')) {
                setShowColorPicker(false);
            }
        };

        if (showColorPicker) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showColorPicker]);

    /**
     * Load custom extensions after the model geometry has finished loading
     * Extensions provide additional functionality like document browser and zoom tools
     */

    function loadCustomExtensions() {
        const extensions = [
            "Autodesk.DocumentBrowser",   // Document navigation and model tree
            "Autodesk.Viewing.ZoomWindow", // Zoom window tool for precise viewing
            "ExternalAttrsExtension",     // Custom extension for external attributes
            // Additional extensions can be enabled as needed:
            // "Autodesk.Sample.CustomPropertyPanelExtension"
            // "AddOns",
            // "Autodesk.ExtensionsPanel",
            // "Autodesk.Vault.Markups",
            // "Autodesk.GG.Markups",
            // "ToolbarExtension"
            // "MyExtension",
            // "Autodesk.Viewing.MarkupsCore",
            // "MarkupExtension"
            // "Autodesk.BIM360.Extension.PushPin"
        ];

        // Load each extension with error handling
        extensions.forEach(extensionName => {
            viewerLeft.current.loadExtension(extensionName)
                .then(function (extensionInstance) {
                    console.log(`Extension loaded: ${extensionName}`, extensionInstance);
                })
                .catch(function (error) {
                    console.error(`Error loading extension: ${extensionName}`, error);
                });
        });
    }


    return (
        // Container div with dynamic height based on viewer initialization state
        <div style={{ width: '100%', height: initialized ? '400px' : '100%' }}>
            <FullScreen handle={fullScreenHandle}>
                <div
                    className={fullScreenHandle.active ? 'col-md-12' : ''}
                    style={{
                        width: '100%',
                        height: fullScreenHandle.active ? '100vh' : '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: fullScreenHandle.active ? '#f0f0f0' : 'transparent'
                    }}
                >
                    {/* Main viewer container where Autodesk Forge Viewer will be rendered */}
                    <div
                        ref={viewerDivLeft}
                        id="viewerDivLeft"
                        style={{
                            width: '100%',
                            height: fullScreenHandle.active ? 'calc(100vh - 80px)' : (initialized ? '400px' : '100%'),
                            backgroundColor: '#f0f0f0',
                            flex: fullScreenHandle.active ? '1' : 'none'
                        }}
                    ></div>

                    {/* Control panel with viewer actions and configuration options - always shown */}
                    <div
                        className="viewerButtonsLeft"
                        style={{
                            position: 'relative',
                            zIndex: 1001,
                            backgroundColor: 'white',
                            padding: '10px',
                            borderTop: '1px solid #ddd',
                            flexShrink: 0
                        }}
                    >
                        {/* Main controls row - all on same line */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '0px', alignItems: 'center', flexWrap: 'nowrap' }}>
                            {/* Open viewer button - shown when viewer is closed and not initializing */}
                            {!initialized && !isInitializing &&
                                <button className="btn btn-secondary" onClick={openViewer}>Open left viewer</button>}

                            {/* Loading state button - shown during token fetch and initialization */}
                            {isInitializing &&
                                <button className="btn btn-secondary" disabled>Loading token...</button>}

                            {/* Close viewer button - shown when viewer is open */}
                            {initialized &&
                                <button className="btn btn-secondary" onClick={closeViewer}>Close left viewer</button>}

                            {/* Dropdown for selecting tag extraction rules */}
                            <select id="rulesDropdown" value={selectedRule} onChange={handleRuleChange} style={{ minWidth: '120px' }}>
                                {rules.map((rule) => (
                                    <option key={rule.value} value={rule.value}>
                                        {rule.label}
                                    </option>
                                ))}
                            </select>

                            {/* Multi-select dropdown for viewer linking modes with visual feedback */}
                            <select
                                id="viewerLinkModeDropdown"
                                value=""
                                onChange={handleSelectOptions}
                                style={{ height: 'auto', minWidth: '120px' }}
                            >
                                {viewerLinkModes.map((mode) => (
                                    <option
                                        key={mode.value}
                                        value={mode.value}
                                        style={{
                                            backgroundColor: selectedOptions.includes(mode.value) ? 'green' : 'white',
                                            color: selectedOptions.includes(mode.value) ? 'white' : 'black'
                                        }}
                                    >
                                        {mode.label}
                                    </option>
                                ))}
                            </select>

                            {/* Property Search Modal Button */}
                            {viewerLoaded && (
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setIsPropertySearchModalOpen(true)}
                                    title="Open Property Search"
                                >
                                    Set colour
                                </button>
                            )}

                            {/* Set colour button for selected objects */}
                            {/*{viewerLoaded && (*/}
                            {/*    <button*/}
                            {/*        className="btn btn-secondary"*/}
                            {/*        onClick={setSelectedToPink}*/}
                            {/*        title="Set selected objects to pink color"*/}
                            {/*    >*/}
                            {/*        Set colour*/}
                            {/*    </button>*/}
                            {/*)}*/}



                            {/* Button to set theming color for specific dbIds */}
                            {/*{initialized && viewerLoaded && (*/}
                            {/*    <button*/}
                            {/*        className="btn btn-warning"*/}
                            {/*        onClick={() => setThemingColor([2220])}*/}
                            {/*        title="Set color"*/}
                            {/*    >*/}
                            {/*        Color dbIds*/}
                            {/*    </button>*/}
                            {/*)}*/}

                            {/* Button to clear all theming colors */}
                            {initialized && viewerLoaded && (
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => clearThemingColors()}
                                    title="Clear colors"
                                >
                                    Clear Colors
                                </button>
                            )}

                            {/* Button to set theming color for dbid 1 */}
                            {/*{initialized && viewerLoaded && (*/}
                            {/*    <button*/}
                            {/*        className="btn btn-warning w-auto"*/}
                            {/*        onClick={() => setThemingColorForDbId1()}*/}
                            {/*        title="set color for dbid 497"*/}
                            {/*    >*/}
                            {/*        Color dbid 497*/}
                            {/*    </button>*/}
                            {/*)}*/}


                            {/* Fullscreen toggle button */}
                            {initialized && (
                                <button
                                    className={`btn ${fullScreenHandle.active ? 'btn-warning' : 'btn-info'}`}
                                    onClick={fullScreenHandle.active ? fullScreenHandle.exit : fullScreenHandle.enter}
                                    title={fullScreenHandle.active ? 'Exit fullscreen' : 'Enter fullscreen'}
                                >
                                    {fullScreenHandle.active ? '🗗' : '🗖'} {fullScreenHandle.active ? 'Exit' : 'Fullscreen'}
                                </button>
                            )}
                        </div>

                        {/* Commented out Assets button - can be enabled when needed */}
                        {/*<button*/}
                        {/*    className="btn btn-secondary w-auto"*/}
                        {/*    onClick={() => setIsModalOpen(true)}*/}
                        {/*    disabled={!urn || urn.trim() === ''}*/}
                        {/*    title={!urn || urn.trim() === '' ? "Please open a file in the left viewer first" : "Open Assets modal"}*/}
                        {/*>*/}
                        {/*    Assets*/}
                        {/*</button>*/}
                    </div>

                    {/* Property Search Floating Panel */}
                    {isPropertySearchModalOpen && (
                        <div
                            style={{
                                position: 'fixed',
                                left: panelPosition.x,
                                top: panelPosition.y,
                                width: '600px',
                                height: '500px',
                                zIndex: 9999,
                                border: '2px solid #007bff',
                                borderRadius: '12px',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)',
                                backgroundColor: 'white',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                {/* Header with drag handle */}
                                <div
                                    onMouseDown={handleMouseDown}
                                    style={{
                                        padding: '16px 20px',
                                        background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                                        borderBottom: '2px solid #0056b3',
                                        borderRadius: '10px 10px 0 0',
                                        cursor: isDragging ? 'grabbing' : 'grab',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        userSelect: 'none',
                                        color: 'white',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <h6 style={{ margin: 0, fontWeight: 'bold', color: 'white', fontSize: '16px' }}>🔍 Property Search</h6>
                                    <button
                                        type="button"
                                        onClick={() => setIsPropertySearchModalOpen(false)}
                                        style={{
                                            background: 'rgba(255,255,255,0.2)',
                                            border: '1px solid rgba(255,255,255,0.3)',
                                            borderRadius: '4px',
                                            fontSize: '16px',
                                            cursor: 'pointer',
                                            color: 'white',
                                            padding: '4px 8px',
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                        title="Close"
                                    >
                                        ×
                                    </button>
                                </div>

                                {/* Content */}
                                <div style={{ padding: '16px', flex: 1, overflow: 'auto' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                                                Property Key
                                            </label>
                                            <select
                                                style={{
                                                    width: '100%',
                                                    padding: '6px 8px',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '4px',
                                                    fontSize: '14px'
                                                }}
                                                value={selectedPropertyKey}
                                                onChange={(e) => setSelectedPropertyKey(e.target.value)}
                                            >
                                                <option value="">Select Property Key</option>
                                                {availablePropertyKeys.map((key) => (
                                                    <option key={key} value={key}>
                                                        {key}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                                                Search Value
                                            </label>
                                            <input
                                                type="text"
                                                style={{
                                                    width: '100%',
                                                    padding: '6px 8px',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '4px',
                                                    fontSize: '14px'
                                                }}
                                                placeholder="Enter search value"
                                                value={searchValue}
                                                onChange={(e) => setSearchValue(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', marginBottom: '16px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                                                Match Type
                                            </label>
                                            <select
                                                style={{
                                                    width: '100%',
                                                    padding: '6px 8px',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '4px',
                                                    fontSize: '14px'
                                                }}
                                                value={matchType}
                                                onChange={(e) => setMatchType(e.target.value)}
                                            >
                                                <option value="partial">Partial Match</option>
                                                <option value="exact">100% Exact Match</option>
                                            </select>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'end' }}>
                                            <button
                                                style={{
                                                    padding: '8px 16px',
                                                    backgroundColor: '#28a745',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    fontWeight: '500'
                                                }}
                                                onClick={() => searchByProperty(selectedPropertyKey, searchValue)}
                                                disabled={!selectedPropertyKey || !searchValue}
                                                onMouseOver={(e) => e.target.style.backgroundColor = '#218838'}
                                                onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
                                            >
                                                Search
                                            </button>
                                        </div>
                                    </div>

                                    {/* Match type explanation */}
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '4px',
                                        marginBottom: '16px',
                                        fontSize: '12px',
                                        color: '#6c757d'
                                    }}>
                                        <div><strong>Partial Match:</strong> Finds values containing the search term</div>
                                        <div><strong>100% Exact Match:</strong> Finds only exact matches</div>
                                    </div>

                                    {/* Search results display */}
                                    {lastSearchResults && (
                                        <div style={{
                                            padding: '12px',
                                            backgroundColor: '#d4edda',
                                            borderRadius: '4px',
                                            border: '1px solid #c3e6cb',
                                            fontSize: '13px',
                                            color: '#155724',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{ marginBottom: '8px' }}>
                                                <strong>Results:</strong> Found {lastSearchResults.resultCount} objects matching
                                                "{lastSearchResults.propertyKey}" = "{lastSearchResults.searchValue}"
                                                ({lastSearchResults.matchType} match)
                                            </div>

                                            {/* Color results section with color selector */}
                                            {lastSearchResults.resultCount > 0 && (
                                                <div>
                                                    {/* Color selection row */}
                                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                                                        <span style={{ fontSize: '12px', fontWeight: '500' }}>Color:</span>

                                                        {/* Predefined color swatches */}
                                                        {[
                                                            { color: '#ffff00', name: 'Yellow' },
                                                            { color: '#ff0000', name: 'Red' },
                                                            { color: '#00ff00', name: 'Green' },
                                                            { color: '#0000ff', name: 'Blue' },
                                                            { color: '#ff8000', name: 'Orange' },
                                                            { color: '#ff00ff', name: 'Magenta' },
                                                            { color: '#00ffff', name: 'Cyan' }
                                                        ].map((colorOption) => (
                                                            <div
                                                                key={colorOption.color}
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    backgroundColor: colorOption.color,
                                                                    border: selectedColor === colorOption.color ? '2px solid #000' : '1px solid #ccc',
                                                                    borderRadius: '3px',
                                                                    cursor: 'pointer',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                                }}
                                                                onClick={() => setSelectedColor(colorOption.color)}
                                                                title={colorOption.name}
                                                            />
                                                        ))}

                                                        {/* HTML5 color picker */}
                                                        <input
                                                            type="color"
                                                            value={selectedColor}
                                                            onChange={(e) => setSelectedColor(e.target.value)}
                                                            style={{
                                                                width: '24px',
                                                                height: '24px',
                                                                border: 'none',
                                                                borderRadius: '3px',
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Choose custom color"
                                                        />
                                                    </div>

                                                    {/* Action buttons */}
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: selectedColor,
                                                                color: selectedColor === '#ffff00' || selectedColor === '#00ff00' || selectedColor === '#00ffff' ? '#000' : '#fff',
                                                                border: '1px solid #ccc',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500'
                                                            }}
                                                            onClick={() => colorSearchResults()}
                                                            title={`Color the search results in ${selectedColor}`}
                                                        >
                                                            🎨 Color Results
                                                        </button>
                                                        <button
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: selectedColor,
                                                                color: selectedColor === '#ffff00' || selectedColor === '#00ff00' || selectedColor === '#00ffff' ? '#000' : '#fff',
                                                                border: '1px solid #ccc',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500'
                                                            }}
                                                            onClick={() => colorSelectedObjects()}
                                                            title={`Color the selected objects in ${selectedColor}`}
                                                        >
                                                            🎯 Color Selected
                                                        </button>
                                                        <button
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: '#6c757d',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500'
                                                            }}
                                                            onClick={() => clearThemingColors()}
                                                            onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                                                            onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                                                            title="Clear all colors"
                                                        >
                                                            🧹 Clear Colors
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Property keys info */}
                                    {availablePropertyKeys.length > 0 && !lastSearchResults && (
                                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                            {availablePropertyKeys.length} property keys available for searching
                                        </div>
                                    )}

                                    {/* Loading state */}
                                    {availablePropertyKeys.length === 0 && (
                                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                            Loading property keys...
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </FullScreen>

            {/* Modal for importing assets - currently not actively used */}
            <ImportAssetsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                viewer={viewerLeft.current}
            />
        </div>
    );
});

ViewerLeft.displayName = 'ViewerLeft';

export default ViewerLeft;
